import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { DashboardGridProps, DashboardTile, DateRange } from '../../types/dashboard';
import { PlatformMapping } from '../../services/api/dashboard.service';
import DashboardTileComponent from './DashboardTile';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './DashboardGrid.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardGridPropsWithDateRange extends DashboardGridProps {
  dateRange?: DateRange;
  onTileRefresh?: (tileId: string) => void;
  refreshKeys?: Record<string, number>;
  platformMapping?: PlatformMapping | null;
  platformMappingLoading?: boolean;
  platformMappingError?: string | null;
  userPrimaryArtistId?: string;
  onRemoveTile?: (tileId: string) => void;
}

const DashboardGrid: React.FC<DashboardGridPropsWithDateRange> = ({
  tiles,
  onLayoutChange,
  onTileToggle,
  isCustomizeMode,
  dateRange,
  onTileRefresh,
  refreshKeys = {},
  platformMapping,
  platformMappingLoading = false,
  platformMappingError = null,
  userPrimaryArtistId,
  onRemoveTile
}) => {
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  const tilesRef = useRef(tiles);
  
  // Update ref when tiles change
  useEffect(() => {
    tilesRef.current = tiles;
  }, [tiles]);

  // Monitor container width changes for better responsiveness
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLayoutChange = useCallback((layout: any, layouts: any) => {
    // Only trigger layout changes when in customize mode and user is actually dragging
    // This prevents auto-saving during window resize or breakpoint changes
    if (!isCustomizeMode) {
      return;
    }
    
    // Use ref to access current tiles without creating dependency cycles
    const updatedTiles = tilesRef.current.map(tile => {
      const layoutItem = layout.find((item: any) => item.i === tile.id);
      return layoutItem ? { ...tile, x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h } : tile;
    });
    onLayoutChange(updatedTiles);
  }, [onLayoutChange, isCustomizeMode]);

  const handleBreakpointChange = useCallback((breakpoint: string) => {
    setCurrentBreakpoint(breakpoint);
  }, []);

  // Utility function to calculate optimal positions with compaction
  const compactLayout = (items: any[], cols: number) => {
    const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
    const grid: boolean[][] = [];
    
    // Initialize grid
    for (let row = 0; row < 50; row++) {
      grid[row] = new Array(cols).fill(false);
    }
    
    return sorted.map(item => {
      // Find the optimal position for this item
      let bestY = 0;
      let bestX = item.x;
      
      // Try to place the item as high as possible
      for (let y = 0; y < 50; y++) {
        for (let x = 0; x <= cols - item.w; x++) {
          // Check if this position is available
          let canPlace = true;
          for (let dy = 0; dy < item.h && canPlace; dy++) {
            for (let dx = 0; dx < item.w && canPlace; dx++) {
              if (grid[y + dy] && grid[y + dy][x + dx]) {
                canPlace = false;
              }
            }
          }
          
          if (canPlace) {
            bestY = y;
            bestX = x;
            break;
          }
        }
        if (bestY === y) break;
      }
      
      // Mark the grid positions as occupied
      for (let dy = 0; dy < item.h; dy++) {
        for (let dx = 0; dx < item.w; dx++) {
          if (!grid[bestY + dy]) grid[bestY + dy] = new Array(cols).fill(false);
          grid[bestY + dy][bestX + dx] = true;
        }
      }
      
      return { ...item, x: bestX, y: bestY };
    });
  };

  // Enhanced layout calculation - use compacted positions directly from tiles, only adjust for responsive breakpoints
  const calculateAdaptiveLayout = useCallback((breakpoint: string, cols: number) => {
    // Sort tiles by their current y position (top to bottom), then by x (left to right)
    // This maintains the compact order set in DashboardPage
    const sortedTiles = [...tiles].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    const layoutItems = sortedTiles.map((tile, index) => {
      const UNIFORM_WIDTH = 6;
      // Use actual tile height instead of hardcoded value
      const UNIFORM_HEIGHT = tile.h || 8;
      
      let adaptiveW, adaptiveH, adaptiveX, adaptiveY;
      
      // For large screens (lg), use the compacted positions directly from tiles
      if (cols >= 12) {
        adaptiveW = tile.w;
        adaptiveH = tile.h;
        adaptiveX = tile.x;
        adaptiveY = tile.y;
      } else if (cols >= 8) {
        // Tablet: 2 per row, maintain 2 columns
        const CHARTS_PER_ROW = 2;
        const row = Math.floor(index / CHARTS_PER_ROW);
        const col = index % CHARTS_PER_ROW;
        adaptiveW = Math.floor(cols / 2); // Half width for 2 per row
        adaptiveX = col * adaptiveW;
        adaptiveH = UNIFORM_HEIGHT;
        adaptiveY = row * UNIFORM_HEIGHT;
      } else if (cols >= 6) {
        // Small tablet: 2 per row, maintain 2 columns
        const CHARTS_PER_ROW = 2;
        const row = Math.floor(index / CHARTS_PER_ROW);
        const col = index % CHARTS_PER_ROW;
        adaptiveW = Math.floor(cols / 2); // Half width for 2 per row
        adaptiveX = col * adaptiveW;
        adaptiveH = UNIFORM_HEIGHT;
        adaptiveY = row * UNIFORM_HEIGHT;
      } else {
        // Mobile: Stack vertically (full width)
        adaptiveW = cols;
        adaptiveX = 0;
        adaptiveH = UNIFORM_HEIGHT;
        adaptiveY = index * UNIFORM_HEIGHT;
      }

      // Ensure tile fits within grid bounds
      if (adaptiveX + adaptiveW > cols) {
        adaptiveX = Math.max(0, cols - adaptiveW);
      }

      return {
        i: tile.id,
        x: adaptiveX,
        y: adaptiveY,
        w: adaptiveW,
        h: adaptiveH,
        minW: adaptiveW,
        minH: adaptiveH,
        maxW: adaptiveW,
        maxH: adaptiveH
      };
    });

    // Apply additional compaction for very small screens
    if (breakpoint === 'xs' || breakpoint === 'xxs') {
      return compactLayout(layoutItems, cols);
    }

    return layoutItems;
  }, [tiles]);

  const layouts = {
    lg: calculateAdaptiveLayout('lg', 12),
    md: calculateAdaptiveLayout('md', 8),
    sm: calculateAdaptiveLayout('sm', 6),
    xs: calculateAdaptiveLayout('xs', 4),
    xxs: calculateAdaptiveLayout('xxs', 2)
  };

  return (
    <div className="dashboard-grid">
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 8, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={currentBreakpoint === 'xs' || currentBreakpoint === 'xxs' ? 60 : 70}
        margin={[
          currentBreakpoint === 'xs' || currentBreakpoint === 'xxs' ? 8 : 16,
          currentBreakpoint === 'xs' || currentBreakpoint === 'xxs' ? 8 : 16
        ]}
        containerPadding={[0, 0]}
        onLayoutChange={handleLayoutChange}
        onBreakpointChange={handleBreakpointChange}
        isDraggable={isCustomizeMode}
        isResizable={false}
        compactType="vertical"
        preventCollision={false}
        autoSize={true}
        useCSSTransforms={true}
        transformScale={1}
        verticalCompact={true}
        allowOverlap={false}
      >
        {tiles.map(tile => (
          <div key={tile.id} className="dashboard-tile-container">
            <DashboardTileComponent
              tile={tile}
              onToggle={() => onTileToggle(tile.id)}
              isCustomizeMode={isCustomizeMode}
              containerWidth={containerWidth}
              currentBreakpoint={currentBreakpoint}
              dateRange={dateRange}
              onRefresh={onTileRefresh}
              refreshKey={refreshKeys[tile.id] || 0}
              platformMapping={platformMapping}
              platformMappingLoading={platformMappingLoading}
              platformMappingError={platformMappingError}
              userPrimaryArtistId={userPrimaryArtistId}
              onRemove={onRemoveTile}
            />
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
};

export default DashboardGrid;
